import { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchSection = ({ onCalculate, loading }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productType, setProductType] = useState("food");
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [justSelected, setJustSelected] = useState(false);

  // Debounce timer for autocomplete
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 3 && !searching && !justSelected) {
        fetchSuggestions(searchQuery);
      } else {
        if (!justSelected) {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searching, justSelected]);

  const fetchSuggestions = async (query) => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(
        `https://world.openfoodfacts.org/cgi/search.pl`,
        {
          params: {
            search_terms: query,
            search_simple: 1,
            action: "process",
            json: 1,
            page_size: 5,
            fields: "product_name,brands,code",
          },
          timeout: 5000, // 5 second timeout for autocomplete
        }
      );

      if (response.data && response.data.products) {
        const uniqueSuggestions = response.data.products
          .filter((p) => p.product_name && p.product_name.trim())
          .slice(0, 5)
          .map((p) => ({
            name: p.product_name,
            brand: p.brands || "",
            code: p.code,
          }));
        
        setSuggestions(uniqueSuggestions);
        setShowSuggestions(uniqueSuggestions.length > 0);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      // Silent fail for autocomplete - don't show errors to user
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.name);
    setShowSuggestions(false);
    setSuggestions([]);
    handleSearch(suggestion.name);
    // Don't reset justSelected flag here - it's handled in handleSearch
  };

  const handleSearch = async (query = searchQuery) => {
    // Ensure query is a string
    const searchTerm = typeof query === 'string' ? query : String(query || '');
    
    if (!searchTerm.trim()) {
      toast.error("Please enter a product name");
      return;
    }

    setSearching(true);
    setSelectedProduct(null);
    setShowSuggestions(false);
    setSuggestions([]);
    setJustSelected(true); // Set flag to prevent suggestions during search
    
    try {
      const response = await axios.get(`${API}/search-product`, {
        params: { query: searchTerm },
        timeout: 25000, // 25 second timeout
      });
      setSearchResults(response.data.results);
      if (response.data.results.length === 0) {
        toast.error("No products found with complete nutritional data. Please try manual entry option.", {
          duration: 5000,
        });
        setJustSelected(false); // Reset if no results
      }
    } catch (error) {
      console.error("Error searching products:", error);
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error("Product search is temporarily slow. Please try manual entry option or try again later.", {
          duration: 6000,
        });
      } else {
        toast.error("Search failed. Please use manual entry option.", {
          duration: 5000,
        });
      }
      setSearchResults([]);
      setJustSelected(false); // Reset on error
    } finally {
      setSearching(false);
    }
  };

  const handleBarcodeScanned = async (barcode) => {
    toast.info(`Barcode detected: ${barcode}`);
    setSearching(true);
    setSelectedProduct(null);
    setSearchResults([]);
    
    try {
      const response = await axios.get(`${API}/search-barcode`, {
        params: { barcode },
      });
      
      if (response.data.found && response.data.product) {
        setSearchResults([response.data.product]);
        setSelectedProduct(response.data.product);
        toast.success(`Product found: ${response.data.product.product_name}`);
      } else {
        toast.error("Product not found in database. Please try manual entry.");
      }
    } catch (error) {
      console.error("Error searching by barcode:", error);
      toast.error("Failed to search by barcode. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setJustSelected(false); // Allow autocomplete again when viewing product details
  };

  const handleCalculateSelected = () => {
    if (!selectedProduct || !selectedProduct.nutrients) {
      toast.error("Please select a product first");
      return;
    }

    const nutrients = {
      ...selectedProduct.nutrients,
      product_type: productType,
    };

    onCalculate(nutrients);
  };

  return (
    <div className="space-y-8">
      {/* Search Bar */}
      <Card className="shadow-xl border-0 bg-white" data-testid="search-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-[#3b444d] mb-3">
            Search for a Product
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Multiple suggestions may be shown. Ensure you select the right product with the right quantities. If in doubt, try manual entry option.
          </p>
          {searchResults.length === 0 && !searching && searchQuery && (
            <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-400 rounded">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Product search relies on external databases which may be slow or have limited UK product data. For best results, use the <strong>Manual Entry</strong> tab or try <strong>Barcode Scanning</strong>.
              </p>
            </div>
          )}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Input
                type="text"
                placeholder="e.g., Coca-Cola, Kellogg's Cornflakes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="h-14 text-lg border-2 focus:border-[#64c2c8]"
                data-testid="search-input"
              />
              
              {/* Autocomplete Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div 
                  className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-[#64c2c8] rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto"
                  data-testid="suggestions-dropdown"
                >
                  {loadingSuggestions && (
                    <div className="p-4 flex items-center justify-center text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Loading suggestions...
                    </div>
                  )}
                  
                  {!loadingSuggestions && suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="p-4 hover:bg-gradient-to-r hover:from-[#64c2c8]/10 hover:to-[#cbcb3c]/10 cursor-pointer border-b border-gray-100 last:border-b-0 transition-all duration-200"
                      data-testid={`suggestion-${index}`}
                    >
                      <div className="font-semibold text-[#3b444d]">
                        {suggestion.name}
                      </div>
                      {suggestion.brand && (
                        <div className="text-sm text-gray-600 mt-1">
                          {suggestion.brand}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button
              onClick={handleSearch}
              disabled={searching}
              className="h-14 px-8 bg-gradient-to-r from-[#64c2c8] to-[#cbcb3c] hover:opacity-90 text-white font-semibold text-lg"
              data-testid="search-button"
            >
              {searching ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              <span className="ml-2">Search</span>
            </Button>
            <Button
              onClick={() => setShowBarcodeScanner(true)}
              className="h-14 px-8 bg-[#e83b84] hover:bg-[#d02f73] text-white font-semibold text-lg"
              data-testid="scan-barcode-button"
            >
              <Camera className="w-5 h-5 mr-2" />
              Scan Barcode
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Barcode Scanner Dialog */}
      <BarcodeScanner
        isOpen={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onBarcodeScanned={handleBarcodeScanned}
      />

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card className="shadow-xl border-0 bg-white" data-testid="search-results">
          <CardContent className="p-8">
            <h3 className="text-xl font-bold text-[#3b444d] mb-6">
              Search Results
            </h3>
            <div className="space-y-4">
              {searchResults.map((product, index) => (
                <div
                  key={index}
                  className={`p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-lg ${
                    selectedProduct === product
                      ? "border-[#64c2c8] bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handleSelectProduct(product)}
                  data-testid={`product-result-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-[#3b444d]">
                        {product.product_name}
                      </h4>
                      {product.brand && (
                        <p className="text-gray-600 mt-1">{product.brand}</p>
                      )}
                      <p className="text-sm text-gray-500 mt-2">
                        Source: {product.source}
                      </p>
                    </div>
                    {product.image_url && (
                      <img
                        src={product.image_url}
                        alt={product.product_name}
                        className="w-20 h-20 object-contain ml-4"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Product Details */}
      {selectedProduct && selectedProduct.nutrients && (
        <Card className="shadow-xl border-2 border-[#cbcb3c] bg-white" data-testid="selected-product-card">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold text-[#3b444d] mb-6">
              Selected Product Details
            </h3>

            {/* Product Type Selection */}
            <div className="mb-8 p-6 bg-gray-50 rounded-xl">
              <Label className="text-lg font-semibold text-[#3b444d] mb-4 block">
                Product Type
              </Label>
              <RadioGroup
                value={productType}
                onValueChange={setProductType}
                className="flex gap-6"
                data-testid="product-type-selector"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="food" id="food" data-testid="product-type-food" />
                  <Label htmlFor="food" className="cursor-pointer text-base">
                    Food (threshold ≥ 4)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="drink" id="drink" data-testid="product-type-drink" />
                  <Label htmlFor="drink" className="cursor-pointer text-base">
                    Drink (threshold ≥ 1)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Nutritional Information */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="p-4 bg-red-50 rounded-lg">
                <h4 className="font-semibold text-[#e83b84] mb-3">
                  A Points (Negative)
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Energy:</span>
                    <span className="font-semibold">
                      {selectedProduct.nutrients.energy_kj} kJ
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturated Fat:</span>
                    <span className="font-semibold">
                      {selectedProduct.nutrients.saturated_fat_g} g
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Free Sugars:</span>
                    <span className="font-semibold">
                      {selectedProduct.nutrients.free_sugars_g} g
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Salt:</span>
                    <span className="font-semibold">
                      {selectedProduct.nutrients.salt_g} g
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-[#cbcb3c] mb-3">
                  C Points (Positive)
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Fruit/Veg/Nut/Seeds:</span>
                    <span className="font-semibold">
                      {selectedProduct.nutrients.fruit_veg_nut_seeds_percent}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Fibre (AOAC):</span>
                    <span className="font-semibold">
                      {selectedProduct.nutrients.fibre_g} g
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Protein:</span>
                    <span className="font-semibold">
                      {selectedProduct.nutrients.protein_g} g
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={handleCalculateSelected}
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-[#e83b84] to-[#64c2c8] hover:opacity-90 text-white font-bold text-lg"
              data-testid="calculate-selected-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : null}
              Calculate Nutrient Profile
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchSection;
